/**
 * Leviathan Web - Theme Context
 * Quasar Prime LLC
 *
 * Loads the current user's appearance settings, applies them to
 * document.documentElement as CSS variables, and persists changes.
 *
 * Color variables are applied in two forms:
 *   --token       full value (hex/rgba)
 *   --token-rgb   "r g b" triple, consumed by the Tailwind color tokens
 *                 defined as `rgb(var(--token-rgb) / <alpha-value>)`
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { palettes, fontSizes, Palette } from '@/styles/palettes';
import { apiGet, apiPut } from '@/utils/api';
import { API_URL } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';

interface ThemeSettings {
  palette: string;
  fontSize: string;
  compactMode: boolean;
  reduceMotion: boolean;
  sidebarCollapsed: boolean;
}

interface ThemeContextType {
  settings: ThemeSettings;
  currentPalette: Palette;
  updateSettings: (settings: Partial<ThemeSettings>) => Promise<void>;
  isLoading: boolean;
}

const defaultSettings: ThemeSettings = {
  palette: 'cyan',
  fontSize: 'medium',
  compactMode: false,
  reduceMotion: false,
  sidebarCollapsed: false,
};

const SETTINGS_ENDPOINT = `${API_URL}/api/users/settings`;

// `cyan` is always present in the palettes map; assert so we have a concrete
// non-undefined fallback under noUncheckedIndexedAccess.
const FALLBACK_PALETTE = palettes.cyan as Palette;

function getPalette(name: string): Palette {
  return palettes[name] ?? FALLBACK_PALETTE;
}

const ThemeContext = createContext<ThemeContextType>({
  settings: defaultSettings,
  currentPalette: FALLBACK_PALETTE,
  updateSettings: async () => {},
  isLoading: true,
});

export const useTheme = () => useContext(ThemeContext);

/**
 * Convert a #rrggbb (or #rgb) hex string into a space-separated RGB triple
 * (e.g. "0 212 255") for use with `rgb(var(--x) / <alpha-value>)`.
 * Returns null for non-hex values (e.g. rgba glow strings).
 */
function hexToRgbTriple(hex: string): string | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  const raw = match?.[1];
  if (!raw) return null;
  const h =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

function applyTheme(settings: ThemeSettings) {
  const palette = getPalette(settings.palette);
  const root = document.documentElement;

  // Apply color variables (both full value and RGB triple where applicable)
  Object.entries(palette.colors).forEach(([key, value]) => {
    // camelCase -> kebab-case CSS variable
    const cssVar = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVar, value);

    const triple = hexToRgbTriple(value);
    if (triple) {
      root.style.setProperty(`${cssVar}-rgb`, triple);
    }
  });

  // Font size
  root.style.setProperty(
    '--font-size-base',
    fontSizes[settings.fontSize as keyof typeof fontSizes] || '16px'
  );

  // Mode class
  root.classList.remove('theme-dark', 'theme-light');
  root.classList.add(`theme-${palette.mode}`);
  // Keep Tailwind's `dark:` variant aligned with the palette mode.
  root.classList.toggle('dark', palette.mode === 'dark');

  // Compact mode
  root.classList.toggle('compact-mode', settings.compactMode);

  // Reduce motion
  root.classList.toggle('reduce-motion', settings.reduceMotion);
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState<ThemeSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  const currentPalette = getPalette(settings.palette);

  // Apply theme whenever settings change (runs even before auth resolves,
  // so the default palette is in place immediately).
  useEffect(() => {
    applyTheme(settings);
  }, [settings]);

  // Load persisted settings once the user is authenticated. We avoid calling
  // the API while logged out, since apiFetch's 401 handler would redirect.
  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      try {
        const { response, data } = await apiGet<Partial<ThemeSettings>>(
          SETTINGS_ENDPOINT
        );
        if (!cancelled && response.ok && data) {
          setSettings({ ...defaultSettings, ...data });
        }
      } catch {
        // Network/parse error - fall back to defaults already in state.
        // eslint-disable-next-line no-console
        console.error('Failed to load theme settings, using defaults');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      setIsLoading(true);
      loadSettings();
    } else {
      // Logged out: reset to defaults, stop loading.
      setSettings(defaultSettings);
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const updateSettings = useCallback(
    async (newSettings: Partial<ThemeSettings>) => {
      // Optimistic update for instant visual feedback.
      const updated = { ...settings, ...newSettings };
      setSettings(updated);

      // Persist in the background.
      try {
        await apiPut(SETTINGS_ENDPOINT, updated);
      } catch {
        // eslint-disable-next-line no-console
        console.error('Failed to save theme settings');
      }
    },
    [settings]
  );

  return (
    <ThemeContext.Provider
      value={{ settings, currentPalette, updateSettings, isLoading }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
